
/* Copyright 2022, Corning Optical Communications
* All Rights Reserved.
*/

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetEventConfigByGroupMutation,
  useGetSnmpEmailGroupsQuery,
  useUpdateEventConfigMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { IEventConfig } from "../../../../store/api/deviceConfig/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { setSeverityValue, setSNMPValue, setEmailValue, setGroupList, setSNMPEmailGroups, setSelectedNotifyGroup } from "../../../../store/api/admin/reducers";
import { severityValueSelector, snmpValueSelector, emailValueSelector } from "../../../../store/api/admin/selectors";
import { setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import { IKeyValue } from "../../../../components/Administrations/types";
import { useDeleteGroupMutation, useGetGroupsQuery } from "../../../../store/api/devices/deviceApiSlice";
import { errorMessage } from "../../../../utils/helpers";
import { INotificationGrpData } from "../../../../store/api/admin/types";
import { ITabItems } from "../../../../components/Shared/Tab/types";
import Events from "../../../../components/Administrations/FaultManagements/Events/Events";
import { Ioptions } from "../../../../components/Shared/Filter/types";
import Devices from "../../../../components/Administrations/FaultManagements/Devices/Devices";
import { setGroupKey } from "../../../../store/filters/devices/NotificationDeviceFilterReducer";

export const useEventConfiguration = () => {
  const dispatch = useStoreDispatch();

  const [filterValue, setFilterValue] = useState({
    severity: "",
    snmp: "",
    email: "",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [configData, setEventConfigData] = useState<IEventConfig[]>([]);
  const [isApplyToAll, setIsApplyToAll] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<INotificationGrpData>({ text: "System Default", value: "NONE" });
  const [isEditGroupSettings, setIsEditGroupSettings] = useState(false);
  const [tabData] = useState<Array<ITabItems>>([
    {
      label: "Events",
      component: React.createElement(Events),
    },
  ]);

  const groups = useGetGroupsQuery();
  const snmpEmailGroups = useGetSnmpEmailGroupsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [updateEventConfig, statusEventConfig] = useUpdateEventConfigMutation();
  const [getEventConfigByGroup, configStatus] = useGetEventConfigByGroupMutation();
  const [deleteGroup, deleteGroupStatus] = useDeleteGroupMutation();

  const severityValue: IKeyValue = useSelector(severityValueSelector);
  const snmpValue = useSelector(snmpValueSelector);
  const emailValue = useSelector(emailValueSelector);

  const fetchEventConfig = useCallback(
    async (value: string) => {
      const group = value === "System Default" ? "NONE" : value;
      try {
        const response: any = await getEventConfigByGroup({ logicalGroup: group });
        if (response?.error || configStatus?.isError) throw response?.error;
        if (!!response?.data && response?.data.length > 0) {
          setEventConfigData(response?.data);
        }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    },
    [dispatch, getEventConfigByGroup]
  );

  useEffect(() => {
    snmpEmailGroups?.data && dispatch(setSNMPEmailGroups(snmpEmailGroups?.data));
  }, [dispatch, snmpEmailGroups?.data]);

  useEffect(() => {
    dispatch(setIsLoadingStatus({ statusEventConfig: statusEventConfig.isLoading }));
  }, [dispatch, statusEventConfig.isLoading]);

  useEffect(() => {
    fetchEventConfig(selectedGroup.text || "NONE");
  }, [fetchEventConfig, selectedGroup.text]);

  useEffect(() => {
    const groupsData = groups.data || [];
    dispatch(setGroupList(groupsData));
  }, [groups.data, dispatch]);

  const applyToAllHandler = () => {
    const appliedToAllData = configData?.map((item: IEventConfig) => {
      let newDataApplyToAll = item;
      if (!!filterValue.severity) {
        dispatch(setSeverityValue({}));
        newDataApplyToAll = { ...newDataApplyToAll, severity: filterValue.severity };
      }
      if (!!filterValue.snmp) {
        dispatch(setSNMPValue({}));
        if (!!!item?.category || item?.category?.toLowerCase() === "custom" || filterValue.snmp === "None") {
          newDataApplyToAll = { ...newDataApplyToAll, snmpManagerGroup: "" };
        } else {
          newDataApplyToAll = { ...newDataApplyToAll, snmpManagerGroup: filterValue.snmp };
        }
      }
      if (!!filterValue.email) {
        dispatch(setEmailValue({}));
        newDataApplyToAll = { ...newDataApplyToAll, emailGroup: filterValue.email === "None" ? "" : filterValue.email };
      }
      return newDataApplyToAll;
    });
    setIsApplyToAll(true);
    setEventConfigData(appliedToAllData);
  };

  const cancelHandler = () => {
    setFilterValue({ severity: "", snmp: "", email: "" });
    dispatch(setSeverityValue({}));
    dispatch(setSNMPValue({}));
    dispatch(setEmailValue({}));
    setEventConfigData(configData);
    fetchEventConfig(selectedGroup.text);
    setIsApplyToAll(false);
  };

  const groupDeleteHandler = async (item: any) => {
    if (!item) return;

    try {
      let response: any;
      response = await deleteGroup(item.value);
      if (response?.error || deleteGroupStatus.error) throw response.error;
      if (response?.data?.statusCodeValue === 200) {
        dispatch(
          setOpenToast({ openToast: true, toastType: "success", message: `Group Notification: ${selectedGroup.text} deleted successfully` })
        );
        groups.refetch();
        handleListItemClick({ text: "System Default", value: "NONE" }, 0);
      }

    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };


  const handleListItemClick = (group: Ioptions, index: number) => {
    fetchEventConfig(group.text);
    setSelectedGroup(group);
    dispatch(setSelectedNotifyGroup(group));
    setSelectedIndex(index);
    dispatch(setGroupKey(group));

    if (group.value !== "NONE" && !tabData.some((t: ITabItems) => t.label === "Devices")) {
      tabData.push({
        label: "Devices",
        component: React.createElement(Devices, { onAssociationChanged: groups.refetch }),
      });
    }
    else if (group.value === "NONE" && tabData.some((t: ITabItems) => t.label === "Devices")) {
      const deviceTabIndex = tabData.findIndex((t: ITabItems) => t.label === "Devices");
      if (deviceTabIndex > -1)
        tabData.splice(deviceTabIndex, 1);
    }
  };

  const submitHandler = async (event: any) => {
    event.preventDefault();
    let result: any;
    let payload: IEventConfig[] = [];

    try {
      if (isApplyToAll) {
        configData.length > 0 &&
          configData.forEach((item: IEventConfig) => {
            payload.push({
              key: item.key,
              logicalGroup: item.logicalGroup,
              severity: item.severity,
              snmpManagerGroup: item.snmpManagerGroup,
              emailGroup: item.emailGroup,
              forwarded: item.forwarded,
              groupName: item?.category || "",
            });
          });
      } else {
        configData.length > 0 &&
          configData.forEach((item: IEventConfig) => {
            let itemvalue = item;
            if (Object.keys(severityValue).includes(item.key)) {
              itemvalue = { ...itemvalue, severity: severityValue[item.key] };
            }
            if (Object.keys(snmpValue).includes(item.key)) {
              itemvalue = { ...itemvalue, snmpManagerGroup: snmpValue[item.key] };
            }
            if (Object.keys(emailValue).includes(item.key)) {
              itemvalue = { ...itemvalue, emailGroup: emailValue[item.key] };
            }
            payload.push({
              key: itemvalue.key,
              logicalGroup: itemvalue.logicalGroup,
              severity: itemvalue.severity,
              snmpManagerGroup: itemvalue.snmpManagerGroup,
              emailGroup: itemvalue.emailGroup,
              forwarded: itemvalue.forwarded,
              groupName: item?.category || "",
            });
            // }
          });
      }
      result = await updateEventConfig(payload);
      if (result?.error || statusEventConfig?.isError) throw result?.error;
      if (result.data?.status === 200 || result.data?.statusCode === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The Event Configuration updates have been initiated.`,
          })
        );
        setIsApplyToAll(false);
        setFilterValue({ severity: "", snmp: "", email: "" });
        dispatch(setSeverityValue({}));
        dispatch(setSNMPValue({}));
        dispatch(setEmailValue({}));
        fetchEventConfig(selectedGroup.text);
      }
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };
  return {
    selectedIndex,
    setSelectedIndex,
    configData,
    configStatus,
    filterValue,
    setFilterValue,
    applyToAllHandler,
    cancelHandler,
    submitHandler,
    selectedGroup,
    setSelectedGroup,
    fetchEventConfig,
    isEditGroupSettings,
    setIsEditGroupSettings,
    groupDeleteHandler,
    statusEventConfig,
    snmpEmailGroups,
    tabData,
    handleListItemClick
  };
};